import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, catchError, filter, forkJoin, map, of, switchMap, takeUntil, timer } from 'rxjs';
import { PatientSummary } from '../models/patient.model';
import { HelpContextService } from '../help/help-context.service';
import { DashboardService } from '../services/dashboard.service';
import { PatientsService } from '../services/patients.service';
import { SoapToHtmlContext, SoapToHtmlPipe } from '../shared/pipes/soap-to-html.pipe';
import {
    formatCombinedAssessmentAndPlan,
    parseCombinedAssessmentAndPlan,
} from '../shared/utils/soap-assessment-plan.util';
import {
    SUBJECTIVE_HEADING_SPECS,
    foldSubjectiveIntoHpiAndRos,
    splitSoapByHeadings,
} from '../shared/utils/soap-heading-split.util';
import { foldObjectiveForDisplay } from '../shared/utils/soap-objective-format.util';
import { applyChartPatientName } from '../shared/utils/chart-patient-name.util';
import { foldPatientInstructionsForDisplay } from '../shared/utils/soap-patient-instructions-format.util';
import { flattenSubjectiveStoryLabels, stripLeadingSectionNumbers } from '../shared/utils/soap-section-format.util';
import { hasDistinctTranslation } from '../shared/utils/transcript-language.util';
import { visitReasonFromSoap } from '../shared/utils/visit-reason.util';
import {
    formatLocalDate,
    formatLocalDateAtTime,
    formatLocalTime,
    parseApiDateTime,
} from '../utils/datetime.util';
import { prefersCompactShell } from '../utils/layout.util';
import {
    AIAnalysisContent,
    TranscriptionDetailService,
} from '../transcriptions/transcription-details-service';
import { clearLocalStorageForPausedTranscriptionDraft } from '../recording/recording-checkpoint.storage';
import {
    PENDING_RECORDING_VISIT_ID,
    SaveTranscriptionClinicalNotesPayload,
    SoapProblem,
    Transcription,
    TranscriptionsService,
    isCheckpointFinalRecordingKey,
    isFailedTranscriptionStatus,
    isPendingRecordingVisitRow,
} from '../transcriptions/transcriptions.service';

interface VisitGroup {
  label: string;
  visits: Transcription[];
}

interface NoteSection {
  id: string;
  title: string;
  body: string;
  collapsed: boolean;
  /** When false, omitted from the copy-all action. Defaults to true. */
  includeInCopyAll?: boolean;
}

const EDITABLE_SECTION_IDS = new Set([
  'hpi',
  'objective',
  'assessment-plan',
  'patient-instructions',
  'treatment-plan-summary',
]);

interface LandingPatientSuggestion extends PatientSummary {
  lastVisitLabel: string;
}

@Component({
  selector: 'app-visits',
  standalone: true,
  imports: [CommonModule, FormsModule, SoapToHtmlPipe],
  templateUrl: './visits.component.html',
  styleUrl: './visits.component.scss',
})
export class VisitsComponent implements OnInit, OnDestroy {
  private readonly dashboard = inject(DashboardService);
  private readonly transcriptionsService = inject(TranscriptionsService);
  private readonly transcriptionDetailService = inject(TranscriptionDetailService);
  private readonly patientsService = inject(PatientsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly helpContext = inject(HelpContextService);
  /** Tells the global Help panel which visit is open so it can attach the visit id to a support request. */
  private readonly publishSelectedVisit = effect(() => this.helpContext.setVisit(this.selectedId()));
  private readonly destroy$ = new Subject<void>();

  @ViewChild('patientSearchInput') patientSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('patientNameEditInput') patientNameEditInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('soapTextarea') soapTextareas?: QueryList<ElementRef<HTMLTextAreaElement>>;

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly visits = signal<Transcription[]>([]);
  readonly selectedId = signal<string | null>(null);
  readonly detailLoading = signal(false);
  /** True while the selected visit is still generating SOAP (show drafting placeholder). */
  readonly draftingNote = signal(false);
  /** True when the selected visit is paused (logo shell with paused treatment). */
  readonly pausedNote = signal(false);
  readonly sections = signal<NoteSection[]>([]);
  readonly noteFeedback = signal<'up' | 'down' | null>(null);
  readonly isReanalyzing = signal(false);
  readonly reanalyzeError = signal<string | null>(null);
  readonly editingSectionId = signal<string | null>(null);
  readonly isSavingNotes = signal(false);
  readonly noteSaveError = signal<string | null>(null);
  private readonly originalSectionBodies = signal<Record<string, string>>({});
  private soapProblems: SoapProblem[] = [];
  private keyFindings: string[] | null = null;
  private aiNotesLoaded = false;
  /** Notes (SOAP) vs the legacy transcription content panel in the detail pane. */
  readonly detailMode = signal<'notes' | 'transcript'>('notes');
  readonly transcriptLoading = signal(false);
  readonly transcriptTab = signal<'translated' | 'original'>('translated');
  readonly translatedTranscript = signal<string | null>(null);
  readonly originalTranscript = signal<string | null>(null);
  readonly transcriptLanguage = signal<string | null>(null);
  private transcriptRequestId = 0;
  private loadedTranscriptVisitId: string | null = null;
  readonly activeOnly = signal(false);
  readonly searchOpen = signal(false);
  readonly searchText = signal('');
  /** Optional status filter applied from Analytics search. */
  readonly statusFilter = signal('');
  /** Surfaced when stop/upload fails after leaving the recording screen. */
  readonly uploadError = this.transcriptionsService.recordingUploadError;

  /** New-visit landing: patient search / add. */
  readonly patientQuery = signal('');
  readonly patientDropdownOpen = signal(false);
  readonly patientHighlight = signal(0);
  readonly patientCreateError = signal<string | null>(null);
  readonly patientCreating = signal(false);
  private readonly allPatients = signal<PatientSummary[]>([]);

  /** Inline patient-name edit on the SOAP detail heading. */
  readonly editingVisitId = signal<string | null>(null);
  readonly editingPatientName = signal('');
  readonly editingSelectedPatient = signal<PatientSummary | null>(null);
  readonly nameEditDropdownOpen = signal(false);
  readonly nameEditHighlight = signal(0);
  readonly savingVisitId = signal<string | null>(null);
  readonly nameEditError = signal<string | null>(null);
  /** Full-screen new-visit landing on compact (mobile) view. */
  readonly mobileLanding = signal(false);
  private openedFromNewQuery = false;
  readonly deleteConfirmVisible = signal(false);
  readonly pendingDeleteVisit = signal<Transcription | null>(null);
  readonly deletingVisitId = signal<string | null>(null);
  readonly deleteError = signal<string | null>(null);

  private readonly refreshing = signal(false);
  private pendingRefreshCoalesce = false;
  /** Visit ids whose SOAP was viewed this session (list polls must not restore bold). */
  private readonly acknowledgedSoapVisitIds = new Set<string>();
  /**
   * Visit the user clicked. List polling must not replace it with the in-progress row.
   * Cleared when a route asks to open a specific visit that is not loaded yet.
   */
  private userPinnedVisitId: string | null = null;
  /**
   * Compact shell only. Set when the user leaves the auto-opened in-progress visit
   * (Back, or opening another card). List polls must not reopen it.
   */
  private suppressProcessingAutoSelect = false;
  /**
   * After Stop, transcription usually runs longer than a minute. Wait before the first list poll,
   * then poll the list only. Visit detail is loaded once that list row is terminal.
   */
  private readonly statusPollDelayAfterStopMs = 60_000;
  private readonly statusPollIntervalMs = 30_000;
  /** Avoid overlapping detail fetches. */
  private detailRequestId = 0;

  /** Shown the moment Stop leaves recording, before the visits list returns. */
  private stopProgressStub: Transcription | null = null;

  readonly selected = computed(() => {
    const id = this.selectedId();
    const found = this.visitsForUi().find((v) => this.visitIdsEqual(v.id, id));
    if (found) {
      return found;
    }
    if (
      id &&
      this.draftingNote() &&
      this.stopProgressStub &&
      this.visitIdsEqual(this.stopProgressStub.id, id)
    ) {
      return this.stopProgressStub;
    }
    // Optimistic Analyzing card was removed before the real visit was selected.
    // Keep the progress screen instead of falling through to patient search.
    if (id && this.draftingNote() && isPendingRecordingVisitRow({ id })) {
      const holdId =
        this.transcriptionsService.notesBuildingVisitId() ||
        this.transcriptionsService.getRecordingUploadSpinnerLinkedRow();
      if (holdId) {
        const real = this.visits().find(
          (v) => this.visitIdsEqual(v.id, holdId) && !isPendingRecordingVisitRow(v)
        );
        if (real) {
          return real;
        }
      }
      if (this.stopProgressStub) {
        return this.stopProgressStub;
      }
    }
    return null;
  });

  readonly hasNoteChanges = computed(() => {
    const orig = this.originalSectionBodies();
    return this.sections().some(
      (s) => this.isEditableSection(s.id) && (orig[s.id] ?? '') !== s.body
    );
  });

  /** API visits plus an optimistic pending row after Stop, until the real transcription shows up. */
  readonly visitsForUi = computed((): Transcription[] => {
    const list = this.visits();
    const pending = this.transcriptionsService.pendingRecordingVisit();
    if (!pending) {
      return list;
    }
    const selectedId = this.selectedId();
    const keepPending =
      selectedId != null && isPendingRecordingVisitRow({ id: selectedId });
    const linked = this.transcriptionsService.getRecordingUploadSpinnerLinkedRow();
    if (!keepPending && linked != null && list.some((v) => String(v.id).trim() === linked)) {
      return list;
    }
    if (list.some((v) => isPendingRecordingVisitRow(v))) {
      return list;
    }
    return [pending, ...list];
  });

  readonly filteredVisits = computed(() => {
    let list = this.visitsForUi();
    if (this.activeOnly()) {
      list = list.filter((v) => {
        const s = this.cardStatus(v);
        return s !== 'Completed' && s !== 'Failed';
      });
    }
    const q = this.searchText().trim().toLowerCase();
    if (q) {
      list = list.filter(
        (v) =>
          (v.patientName ?? '').toLowerCase().includes(q) ||
          (v.chiefComplaint ?? '').toLowerCase().includes(q) ||
          (v.visitReason ?? '').toLowerCase().includes(q) ||
          (v.mrn ?? '').toLowerCase().includes(q) ||
          this.cardStatus(v).toLowerCase().includes(q)
      );
    }
    const status = this.statusFilter().trim();
    if (status && status !== 'All') {
      list = list.filter((v) => {
        const s = this.cardStatus(v);
        if (status === 'In Progress') {
          return this.isPipelineStatus(s) || s === 'In Progress';
        }
        return s === status;
      });
    }
    return list;
  });

  readonly groups = computed((): VisitGroup[] => {
    const byDay = new Map<string, Transcription[]>();
    for (const v of this.filteredVisits()) {
      const key = this.dayKey(v);
      const bucket = byDay.get(key) ?? [];
      bucket.push(v);
      byDay.set(key, bucket);
    }
    return [...byDay.entries()].map(([label, visits]) => ({ label, visits }));
  });

  readonly landingSuggestions = computed((): LandingPatientSuggestion[] => {
    const q = this.patientQuery().trim().toLowerCase();
    if (!q) {
      return [];
    }
    const matched = this.allPatients()
      .filter((p) => {
        const full = `${p.firstName} ${p.lastName}`.toLowerCase();
        const mrn = (p.mrn ?? '').toLowerCase();
        return full.includes(q) || mrn.includes(q);
      })
      .slice(0, 8);
    return matched.map((p) => ({
      ...p,
      lastVisitLabel: this.lastVisitLabelForPatient(p),
    }));
  });

  readonly showAddPatientOption = computed(() => this.patientQuery().trim().length > 0);

  readonly nameEditSuggestions = computed((): LandingPatientSuggestion[] => {
    const q = this.editingPatientName().trim().toLowerCase();
    if (!q) {
      return [];
    }
    return this.allPatients()
      .filter((p) => {
        const full = `${p.firstName} ${p.lastName}`.toLowerCase();
        const mrn = (p.mrn ?? '').toLowerCase();
        return full.includes(q) || mrn.includes(q);
      })
      .slice(0, 8)
      .map((p) => ({
        ...p,
        lastVisitLabel: this.lastVisitLabelForPatient(p),
      }));
  });

  ngOnInit(): void {
    // If we just landed from Stop, show the optimistic card immediately.
    const pending = this.transcriptionsService.pendingRecordingVisit();
    const buildingId = this.transcriptionsService.notesBuildingVisitId();
    if (pending) {
      this.loading.set(false);
      this.stopProgressStub = pending;
      this.selectedId.set(pending.id);
      this.showDraftingShell();
    } else if (buildingId) {
      this.loading.set(false);
      this.selectedId.set(buildingId);
      this.stopProgressStub = {
        id: buildingId,
        date: '',
        specialty: '',
        duration: '',
        status: 'Analyzing',
        patientName: '',
      };
      this.showDraftingShell();
    }

    this.loadVisits({ endInitialLoad: true });
    this.loadPatientSummaries();

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const toClear: Record<string, null> = {};
      if (params.get('new') != null) {
        this.openedFromNewQuery = true;
        this.openNewVisitLanding();
      } else if (this.openedFromNewQuery) {
        this.openedFromNewQuery = false;
        this.mobileLanding.set(false);
      }
      if (params.get('list') != null) {
        this.onMobileBack();
        toClear['list'] = null;
      }
      const search = params.get('search');
      if (search) {
        this.searchOpen.set(true);
        this.searchText.set(search);
        toClear['search'] = null;
      }
      const status = params.get('status');
      if (status) {
        this.statusFilter.set(status);
        toClear['status'] = null;
      }
      const visitId = params.get('visit');
      if (visitId) {
        const existing = this.visitsForUi().find((v) => this.visitIdsEqual(v.id, visitId));
        if (existing) {
          this.pinUserVisit(existing);
          this.selectVisit(existing);
        } else {
          this.userPinnedVisitId = null;
          this.transcriptionsService.requestSelectVisitOnLoad(visitId);
        }
        toClear['visit'] = null;
      }
      if (Object.keys(toClear).length) {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: toClear,
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });

    this.transcriptionsService.transcriptionsListRefreshRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshVisits());

    // One list poll. A visit just stopped waits a minute; an already-running visit checks every 30s.
    const firstPollMs = this.transcriptionJustStarted()
      ? this.statusPollDelayAfterStopMs
      : this.statusPollIntervalMs;
    timer(firstPollMs, this.statusPollIntervalMs)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.loading()),
        filter(() => !this.refreshing()),
        filter(() => this.shouldAutoRefreshVisits()),
        switchMap(() => this.dashboard.getTranscriptions())
      )
      .subscribe({
        next: (rows) =>
          this.applyVisitsFromApi(rows, {
            consumeUploadAwaiting: true,
            keepSelection: true,
          }),
        error: () => {
          /* keep showing last good list */
        },
      });
  }

  /** True when this page opened because Stop just started a transcription. */
  private transcriptionJustStarted(): boolean {
    return (
      this.transcriptionsService.isRecordingUploadAwaitingActive() ||
      !!this.transcriptionsService.pendingRecordingVisit() ||
      !!this.transcriptionsService.notesBuildingVisitId()
    );
  }

  ngOnDestroy(): void {
    this.helpContext.setVisit(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadVisits(options: { endInitialLoad?: boolean } = {}): void {
    this.dashboard
      .getTranscriptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) =>
          this.applyVisitsFromApi(rows, {
            consumeUploadAwaiting:
              this.transcriptionsService.isRecordingUploadAwaitingActive() ||
              this.transcriptionsService.peekRecordingUploadClearScheduled(),
            endInitialLoad: options.endInitialLoad,
            // Initial load stays on the patient landing; only jump to newest after Stop.
            preferNewest:
              !!this.transcriptionsService.pendingRecordingVisit() ||
              this.transcriptionsService.isRecordingUploadAwaitingActive() ||
              !!this.transcriptionsService.peekSelectVisitOnLoad() ||
              !!this.transcriptionsService.getRecordingUploadSpinnerLinkedRow() ||
              !!this.transcriptionsService.notesBuildingVisitId(),
            keepSelection: true,
          }),
        error: () => {
          this.error.set('Could not load visits.');
          this.loading.set(false);
        },
      });
  }

  private refreshVisits(): void {
    if (this.refreshing()) {
      this.pendingRefreshCoalesce = true;
      return;
    }
    this.refreshing.set(true);
    const preferNewest =
      !!this.transcriptionsService.pendingRecordingVisit() ||
      this.transcriptionsService.isRecordingUploadAwaitingActive() ||
      !!this.transcriptionsService.peekSelectVisitOnLoad() ||
      !!this.transcriptionsService.getRecordingUploadSpinnerLinkedRow() ||
      !!this.transcriptionsService.notesBuildingVisitId();
    this.dashboard
      .getTranscriptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.applyVisitsFromApi(rows, {
            consumeUploadAwaiting: true,
            preferNewest,
            keepSelection: !preferNewest,
          });
          this.refreshing.set(false);
          if (this.pendingRefreshCoalesce) {
            this.pendingRefreshCoalesce = false;
            this.refreshVisits();
          }
        },
        error: () => {
          this.refreshing.set(false);
          if (this.transcriptionsService.consumeClearRecordingUploadAwaitingAfterRefresh()) {
            this.transcriptionsService.setRecordingUploadAwaitingResult(false);
          }
          if (this.pendingRefreshCoalesce) {
            this.pendingRefreshCoalesce = false;
            this.refreshVisits();
          }
        },
      });
  }

  private shouldAutoRefreshVisits(): boolean {
    if (this.transcriptionsService.isRecordingUploadAwaitingActive()) {
      return true;
    }
    if (this.transcriptionsService.peekSelectVisitOnLoad()) {
      return true;
    }
    if (this.transcriptionsService.pendingRecordingVisit()) {
      return true;
    }
    // Use the API status. cardStatus stays "Analyzing" while notesBuilding is set, which
    // would keep this poll alive after the visit is already Completed.
    return this.visitsForUi().some(
      (v) => isPendingRecordingVisitRow(v) || this.isPipelineStatus(this.displayStatus(v.status))
    );
  }

  private applyVisitsFromApi(
    rows: Transcription[],
    options: {
      consumeUploadAwaiting?: boolean;
      endInitialLoad?: boolean;
      preferNewest?: boolean;
      keepSelection?: boolean;
    }
  ): void {
    const currentId = this.selectedId();
    const prevSelected =
      currentId != null ? this.visitsForUi().find((v) => this.visitIdsEqual(v.id, currentId)) : null;
    const previousIds = new Set(this.visits().map((v) => String(v.id).trim()));
    const sorted = [...rows]
      .sort((a, b) => this.visitTime(b) - this.visitTime(a))
      .map((v) => {
        if (
          this.isPipelineStatus(this.displayStatus(v.status)) ||
          this.transcriptionsService.isNotesBuilding(v.id)
        ) {
          this.acknowledgedSoapVisitIds.delete(v.id);
          return v;
        }
        if (this.acknowledgedSoapVisitIds.has(v.id) && v.soapUnread === true) {
          return { ...v, soapUnread: false };
        }
        return v;
      });
    this.visits.set(sorted);
    this.error.set(null);
    if (options.endInitialLoad) {
      this.loading.set(false);
    }

    this.maybeClearRecordingUploadSpinnerFromList(sorted);
    this.maybeClearPendingRecordingVisit(sorted, previousIds);
    if (options.consumeUploadAwaiting) {
      if (this.transcriptionsService.consumeClearRecordingUploadAwaitingAfterRefresh()) {
        this.transcriptionsService.setRecordingUploadAwaitingResult(false);
      }
    }

    const uiList = this.visitsForUi();
    if (!uiList.length) {
      this.selectedId.set(null);
      this.clearNoteShell();
      if (options.endInitialLoad && !this.isCompactShell()) {
        queueMicrotask(() => this.patientSearchInput?.nativeElement.focus());
      }
      return;
    }

    const linked = this.transcriptionsService.getRecordingUploadSpinnerLinkedRow();
    const forcedId =
      this.transcriptionsService.peekSelectVisitOnLoad() ||
      linked ||
      this.transcriptionsService.notesBuildingVisitId();
    const forcedVisit =
      forcedId != null
        ? uiList.find((v) => this.visitIdsEqual(v.id, forcedId) && !isPendingRecordingVisitRow(v))
        : undefined;
    const stillPresent = currentId != null && uiList.some((v) => this.visitIdsEqual(v.id, currentId));
    const wasPending = currentId != null && isPendingRecordingVisitRow({ id: currentId });
    const firstProcessing = this.firstProcessingVisit(uiList);

    if (forcedVisit) {
      this.patchVisitStatusIfNotesBuilding(sorted);
      if (this.blockForcedProcessingSelection(forcedVisit.id, stillPresent, wasPending)) {
        const next =
          currentId != null ? sorted.find((v) => this.visitIdsEqual(v.id, currentId)) : undefined;
        if (next) {
          this.reloadDetailIfStatusChanged(prevSelected, next);
        }
      } else if (!this.visitIdsEqual(this.selectedId(), forcedVisit.id)) {
        this.selectVisit(forcedVisit);
      } else {
        this.reloadDetailIfStatusChanged(prevSelected, forcedVisit);
      }
      this.transcriptionsService.clearSelectVisitOnLoad();
      this.transcriptionsService.setRecordingUploadSpinnerLinkedRow(null);
    } else if (forcedId) {
      // Target visit not in the list yet (just paused/stopped) — keep polling.
    } else if (currentId == null && !this.keepCompactListSelection()) {
      // Stay on the new-visit landing unless we just finished a recording.
      if (options.preferNewest && firstProcessing) {
        this.selectVisit(firstProcessing);
      } else if (options.preferNewest) {
        this.selectVisit(uiList[0]);
      } else if (options.endInitialLoad && !this.isCompactShell()) {
        queueMicrotask(() => this.patientSearchInput?.nativeElement.focus());
      }
    } else if (options.preferNewest && wasPending) {
      // Keep the optimistic pending card selected until a processing row exists.
    } else if (options.preferNewest && !stillPresent && !this.keepCompactListSelection()) {
      this.selectVisit(firstProcessing ?? uiList[0]);
    } else if (!stillPresent && !wasPending) {
      if (this.isCompactShell()) {
        this.selectedId.set(null);
        this.clearNoteShell();
        this.mobileLanding.set(false);
      } else {
        this.selectVisit(uiList[0]);
      }
    } else if (currentId && !isPendingRecordingVisitRow({ id: currentId })) {
      this.patchVisitStatusIfNotesBuilding(sorted);
      const next = sorted.find((v) => this.visitIdsEqual(v.id, currentId));
      if (next) {
        this.reloadDetailIfStatusChanged(prevSelected, next);
      }
    } else {
      this.patchVisitStatusIfNotesBuilding(sorted);
    }
  }

  /** Fetch visit detail when list API status changes — not on every poll. */
  private reloadDetailIfStatusChanged(
    prevSelected: Transcription | undefined,
    next: Transcription
  ): void {
    const prevApi = this.displayStatus((prevSelected ?? next).status);
    const nextApi = this.displayStatus(next.status);
    const building = this.transcriptionsService.isNotesBuilding(next.id);
    const terminal = this.isTerminalStatus(nextApi);

    // Resume/append still looks Paused or Completed until the server starts the new job.
    if (building && !this.notesBuildingMayFinish(next.id)) {
      this.showDraftingShell();
      return;
    }

    // cardStatus maps Completed → Analyzing while a rebuild is still waiting on Analyzing.
    if (building && terminal && this.notesBuildingMayFinish(next.id)) {
      this.loadDetail(next.id);
      return;
    }
    if (this.draftingNote() && terminal && !this.isPipelineStatus(nextApi)) {
      this.loadDetail(next.id);
      return;
    }

    if (this.isPipelineStatus(nextApi) || (building && !terminal)) {
      this.showDraftingShell();
      return;
    }
    if (prevApi === nextApi) {
      if (nextApi === 'Paused' && !building) {
        this.showPausedShell();
      }
      return;
    }
    this.loadDetail(next.id);
  }

  /** New recordings can finish on first Completed; rebuilds must see Analyzing first. */
  private notesBuildingMayFinish(id: string): boolean {
    if (!this.transcriptionsService.isNotesBuilding(id)) {
      return false;
    }
    if (!this.transcriptionsService.isNotesBuildingRebuild()) {
      return true;
    }
    return this.transcriptionsService.hasNotesBuildingSeenPipeline(id);
  }

  /** Track pipeline progress while notes generate; do not overwrite API Completed. */
  private patchVisitStatusIfNotesBuilding(sorted: Transcription[]): void {
    const buildingId = this.transcriptionsService.notesBuildingVisitId();
    if (!buildingId) {
      return;
    }
    const row = sorted.find((v) => this.visitIdsEqual(v.id, buildingId));
    if (!row) {
      return;
    }
    const apiStatus = this.displayStatus(row.status);
    if (this.isPipelineStatus(apiStatus)) {
      this.transcriptionsService.markNotesBuildingPipelineSeen(buildingId);
      return;
    }
    // A rebuild just started is still the previous Completed/Paused row. Clearing here
    // shows that old note until the new job is visible.
    if (
      this.transcriptionsService.isNotesBuildingRebuild() &&
      !this.transcriptionsService.hasNotesBuildingSeenPipeline(buildingId)
    ) {
      return;
    }
    // Analysis finished (or failed) even if this client never observed Analyzing.
    // Otherwise notesBuilding stays set and the visit card stays on Analyzing.
    if (!this.isTerminalStatus(apiStatus)) {
      return;
    }
    this.transcriptionsService.clearNotesBuilding(buildingId);
    if (this.visitIdsEqual(this.selectedId(), buildingId) && this.draftingNote()) {
      this.loadDetail(buildingId);
    }
  }

  /** Newest-first processing visit (Analyzing, Transcribing, or In Progress). */
  private firstProcessingVisit(rows: Transcription[]): Transcription | undefined {
    return rows.find(
      (v) => !isPendingRecordingVisitRow(v) && this.isPipelineStatus(this.displayStatus(v.status))
    );
  }

  private maybeClearPendingRecordingVisit(sorted: Transcription[], previousIds: Set<string>): void {
    if (!this.transcriptionsService.pendingRecordingVisit()) {
      return;
    }
    const linked = this.transcriptionsService.getRecordingUploadSpinnerLinkedRow();
    if (linked != null) {
      const linkedVisit = sorted.find((v) => this.visitIdsEqual(v.id, linked));
      if (linkedVisit) {
        this.transcriptionsService.requestSelectVisitOnLoad(linkedVisit.id);
        this.transcriptionsService.clearPendingRecordingVisit();
        return;
      }
    }
    const pending = this.transcriptionsService.pendingRecordingVisit();
    if (!pending) {
      return;
    }
    const processing = sorted.filter(
      (v) => !isPendingRecordingVisitRow(v) && this.isPipelineStatus(this.displayStatus(v.status))
    );
    if (!processing.length) {
      return;
    }
    const newlyAppeared =
      previousIds.size === 0
        ? []
        : processing.filter((v) => !previousIds.has(String(v.id).trim()));
    const pendingTime = this.visitTime(pending);
    const recent = processing.filter((v) => {
      const t = this.visitTime(v);
      const comparable = t > 0 ? t : pendingTime;
      return comparable >= pendingTime - 15_000;
    });
    // Bind as soon as the new row exists (Analyzing is enough). If several are in flight, focus the first.
    const match = newlyAppeared[0] ?? recent[0];
    if (!match) {
      return;
    }
    this.transcriptionsService.requestSelectVisitOnLoad(match.id);
    this.transcriptionsService.clearPendingRecordingVisit();
  }

  private maybeClearRecordingUploadSpinnerFromList(sorted: Transcription[]): void {
    if (!this.transcriptionsService.isRecordingUploadAwaitingActive()) {
      return;
    }
    const linked = this.transcriptionsService.getRecordingUploadSpinnerLinkedRow();
    const scheduled = this.transcriptionsService.peekRecordingUploadClearScheduled();
    const linkedMatch =
      linked != null && sorted.some((v) => this.visitIdsEqual(v.id, linked));
    const pipelineVisible = sorted.some((v) => {
      const s = this.displayStatus(v.status);
      return this.isPipelineStatus(s) || s === 'Needs Review';
    });
    if (linkedMatch || (scheduled && pipelineVisible)) {
      this.transcriptionsService.consumeClearRecordingUploadAwaitingAfterRefresh();
      this.transcriptionsService.setRecordingUploadAwaitingResult(false);
    }
  }

  isSelected(visit: Transcription): boolean {
    return this.visitIdsEqual(this.selectedId(), visit.id);
  }

  selectVisit(visit: Transcription): void {
    if (this.editingVisitId() && !this.visitIdsEqual(this.editingVisitId(), visit.id)) {
      this.cancelPatientNameEdit();
    }
    this.mobileLanding.set(false);
    this.detailMode.set('notes');
    this.noteFeedback.set(null);
    this.isReanalyzing.set(false);
    this.reanalyzeError.set(null);
    this.resetNoteEditorState();
    this.transcriptLanguage.set(null);
    this.selectedId.set(String(visit.id));
    if (isPendingRecordingVisitRow(visit)) {
      this.stopProgressStub = visit;
      this.showDraftingShell();
      this.detailLoading.set(false);
      return;
    }
    const status = this.cardStatus(visit);
    // Show building-notes / paused shell immediately (avoid blank Objective / stale SOAP flash).
    const apiStatus = this.displayStatus(visit.status);
    const stillWorking =
      this.isPipelineStatus(apiStatus) ||
      (this.transcriptionsService.isNotesBuilding(visit.id) &&
        !this.notesBuildingMayFinish(visit.id));
    if (stillWorking) {
      // The list poll's status is enough to know the visit is still running. Load the note once it finishes.
      this.showDraftingShell();
      this.detailLoading.set(false);
      if (this.isPipelineStatus(apiStatus)) {
        this.transcriptionsService.markNotesBuildingPipelineSeen(visit.id);
      }
      return;
    } else if (status === 'Paused') {
      this.showPausedShell();
      this.detailLoading.set(false);
      return;
    } else {
      this.draftingNote.set(false);
      this.pausedNote.set(false);
    }
    this.loadDetail(visit.id);
  }

  dismissUploadError(): void {
    this.transcriptionsService.clearRecordingUploadError();
  }

  toggleSection(id: string): void {
    this.sections.update((list) =>
      list.map((s) => (s.id === id ? { ...s, collapsed: !s.collapsed } : s))
    );
  }

  soapHtmlContext(section: NoteSection): SoapToHtmlContext {
    if (section.id === 'hpi') {
      return 'hpi';
    }
    if (section.id === 'objective') {
      return 'objective';
    }
    if (section.id === 'patient-instructions') {
      return 'patientInstructions';
    }
    if (section.id === 'treatment-plan-summary') {
      return 'treatmentPlanSummary';
    }
    if (section.id === 'assessment-plan') {
      return 'assessmentPlan';
    }
    return 'default';
  }

  async copySection(body: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(body);
    } catch {
      /* ignore */
    }
  }

  canCopyAllNotes(): boolean {
    return (
      !this.draftingNote() &&
      !this.pausedNote() &&
      !this.detailLoading() &&
      this.sections().some((s) => s.includeInCopyAll !== false && !!s.body)
    );
  }

  async copyAllNotes(): Promise<void> {
    const text = this.sections()
      .filter((s) => s.includeInCopyAll !== false)
      .map((s) => [s.title, s.body].filter(Boolean).join('\n'))
      .filter(Boolean)
      .join('\n\n');
    await this.copySection(text);
  }

  isEditableSection(id: string): boolean {
    return EDITABLE_SECTION_IDS.has(id);
  }

  toggleSectionEdit(section: NoteSection): void {
    if (!this.isEditableSection(section.id) || this.isSavingNotes()) {
      return;
    }
    if (this.editingSectionId() === section.id) {
      this.editingSectionId.set(null);
      return;
    }
    if (section.collapsed) {
      this.toggleSection(section.id);
    }
    this.editingSectionId.set(section.id);
    setTimeout(() => this.resizeEditingTextarea(), 0);
  }

  onSectionBodyInput(sectionId: string, event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.sections.update((list) =>
      list.map((s) => (s.id === sectionId ? { ...s, body: target.value } : s))
    );
    this.autoResizeTextarea(target);
  }

  onResetNotes(): void {
    if (this.isSavingNotes()) {
      return;
    }
    const orig = this.originalSectionBodies();
    this.editingSectionId.set(null);
    this.noteSaveError.set(null);
    this.sections.update((list) =>
      list.map((s) => (s.id in orig ? { ...s, body: orig[s.id] } : s))
    );
  }

  onSaveNotes(): void {
    const visit = this.selected();
    if (
      !visit ||
      isPendingRecordingVisitRow(visit) ||
      this.isSavingNotes() ||
      !this.hasNoteChanges()
    ) {
      return;
    }
    this.isSavingNotes.set(true);
    this.noteSaveError.set(null);
    this.editingSectionId.set(null);
    this.transcriptionsService
      .saveTranscriptionClinicalNotes(visit.id, this.buildNotesPayload())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSavingNotes.set(false);
          this.captureOriginalBodies(this.sections(), true);
        },
        error: (error) => {
          this.isSavingNotes.set(false);
          const status = error?.status;
          this.noteSaveError.set(
            status === 404
              ? 'No analysis record found yet for this visit. Try again after analysis completes.'
              : 'Could not save notes. Please try again.'
          );
        },
      });
  }

  onReanalyze(): void {
    const visit = this.selected();
    if (!visit || isPendingRecordingVisitRow(visit) || this.isReanalyzing()) {
      return;
    }
    this.isReanalyzing.set(true);
    this.reanalyzeError.set(null);
    this.transcriptionsService
      .reanalyzeTranscription(visit.id, visit.specialty)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isReanalyzing.set(false);
          this.transcriptionsService.markNotesBuilding(visit.id, { rebuild: true });
          this.showDraftingShell();
          this.refreshVisits();
        },
        error: (err) => {
          this.isReanalyzing.set(false);
          const summary = err?.error?.summary ?? err?.error?.message;
          this.reanalyzeError.set(
            typeof summary === 'string' && summary.trim()
              ? summary.trim()
              : 'Failed to re-analyze this visit. Please try again.'
          );
        },
      });
  }

  setNoteFeedback(value: 'up' | 'down'): void {
    this.noteFeedback.update((current) => (current === value ? null : value));
  }

  onNewVisit(): void {
    this.openNewVisitLanding();
  }

  openNewVisitLanding(): void {
    this.cancelPatientNameEdit();
    this.selectedId.set(null);
    this.clearNoteShell();
    this.mobileLanding.set(true);
    this.patientQuery.set('');
    this.patientCreateError.set(null);
    this.patientDropdownOpen.set(false);
    this.patientHighlight.set(0);
    queueMicrotask(() => this.patientSearchInput?.nativeElement.focus());
  }

  onMobileBack(): void {
    this.cancelPatientNameEdit();
    this.selectedId.set(null);
    this.clearNoteShell();
    this.mobileLanding.set(false);
    this.suppressProcessingAutoSelect = true;
  }

  onLandingBack(): void {
    this.onMobileBack();
    void this.router.navigate(['/'], { queryParams: { list: '1' } });
  }

  private isCompactShell(): boolean {
    return prefersCompactShell();
  }

  onPatientSearchFocus(): void {
    if (this.patientQuery().trim()) {
      this.patientDropdownOpen.set(true);
    }
  }

  onPatientQueryChange(value: string): void {
    this.patientQuery.set(value);
    this.patientDropdownOpen.set(value.trim().length > 0);
    this.patientHighlight.set(0);
    this.patientCreateError.set(null);
  }

  onPatientSearchKeydown(event: KeyboardEvent): void {
    const suggestions = this.landingSuggestions();
    const addOffset = this.showAddPatientOption() ? 1 : 0;
    const total = suggestions.length + addOffset;
    if (!total && event.key !== 'Escape') {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.patientDropdownOpen.set(true);
      this.patientHighlight.set((this.patientHighlight() + 1) % Math.max(total, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.patientDropdownOpen.set(true);
      this.patientHighlight.set((this.patientHighlight() - 1 + total) % Math.max(total, 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const idx = this.patientHighlight();
      if (idx < suggestions.length) {
        this.selectLandingPatient(suggestions[idx]);
      } else if (this.showAddPatientOption()) {
        this.addNewPatientFromQuery();
      }
    } else if (event.key === 'Escape') {
      this.patientDropdownOpen.set(false);
    }
  }

  selectLandingPatient(patient: PatientSummary): void {
    this.patientDropdownOpen.set(false);
    void this.router.navigate(['/recording'], {
      queryParams: { patientId: patient.id },
    });
  }

  addNewPatientFromQuery(): void {
    const name = this.patientQuery().trim();
    if (!name || this.patientCreating()) {
      return;
    }
    const parts = name.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? name;
    const lastName = parts.slice(1).join(' ') || firstName;
    this.createPatientAndStartRecording(firstName, lastName);
  }

  continueWithoutPatient(): void {
    if (this.patientCreating()) {
      return;
    }
    this.createPatientAndStartRecording('Unknown', 'Unknown');
  }

  private createPatientAndStartRecording(firstName: string, lastName: string): void {
    this.patientDropdownOpen.set(false);
    this.patientCreateError.set(null);
    this.patientCreating.set(true);

    this.patientsService.createPatient({ firstName, lastName }).subscribe({
      next: (patient) => {
        this.patientCreating.set(false);
        const summary: PatientSummary = {
          id: patient.id,
          firstName: patient.firstName,
          lastName: patient.lastName,
          mrn: patient.mrn,
          displayName: `${patient.firstName} ${patient.lastName}`.trim(),
        };
        this.allPatients.set([
          summary,
          ...this.allPatients().filter((p) => p.id !== patient.id),
        ]);
        this.patientQuery.set('');
        void this.router.navigate(['/recording'], {
          queryParams: { patientId: patient.id, autoStart: '1' },
        });
      },
      error: (err) => {
        this.patientCreating.set(false);
        this.patientCreateError.set(
          err?.error?.message ?? err?.message ?? 'Could not create patient. Try again.'
        );
      },
    });
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.visits__landing-search')) {
      this.patientDropdownOpen.set(false);
    }
    if (!target?.closest('.visits__detail-name-edit')) {
      this.nameEditDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.deleteConfirmVisible()) {
      this.cancelDeleteConfirm();
    }
  }

  private loadPatientSummaries(): void {
    this.patientsService
      .getPatientSummaries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (patients) => {
          this.allPatients.set(patients ?? []);
        },
        error: () => {
          this.allPatients.set([]);
        },
      });
  }

  private lastVisitLabelForPatient(patient: PatientSummary): string {
    const fullName = `${patient.firstName} ${patient.lastName}`.trim().toLowerCase();
    const matches = this.visits().filter((v) => {
      if (v.patientId != null && String(v.patientId) === String(patient.id)) {
        return true;
      }
      return (v.patientName ?? '').trim().toLowerCase() === fullName;
    });
    if (!matches.length) {
      return 'No visits yet';
    }
    const newest = [...matches].sort((a, b) => this.visitTime(b) - this.visitTime(a))[0];
    const d = parseApiDateTime(newest.appointmentTime ?? newest.date);
    if (!d) {
      return 'Last visit: —';
    }
    return `Last visit: ${formatLocalDate(d, { year: 'numeric' })}`;
  }

  /** Paused draft, or a visit that already has a generated note (append more audio). */
  canResumeVisit(visit: Transcription): boolean {
    const s = this.cardStatus(visit);
    if (s === 'Paused') {
      return true;
    }
    if (this.transcriptionsService.isNotesBuilding(visit.id) || this.isPipelineStatus(s)) {
      return false;
    }
    if (s === 'Completed' || s === 'Needs Review' || s === 'Failed') {
      // Append needs the extendable final recording key on the visit.
      return isCheckpointFinalRecordingKey(visit.originalFileName);
    }
    return false;
  }

  onResume(visit: Transcription): void {
    if (!this.canResumeVisit(visit)) {
      return;
    }
    const s = this.cardStatus(visit);
    if (s === 'Paused') {
      void this.router.navigate(['/recording'], {
        queryParams: { resumeTranscription: visit.id },
      });
      return;
    }
    // Already-generated note: Resume means add more audio into this visit.
    void this.router.navigate(['/recording'], {
      queryParams: { appendTranscription: visit.id },
    });
  }

  /** List/detail label — never "Recording"; uses pipeline names or forced Analyzing while rebuilding. */
  cardStatus(visit: Transcription): string {
    if (this.transcriptionsService.isNotesBuilding(visit.id)) {
      const api = this.displayStatus(visit.status);
      if (this.isPipelineStatus(api)) {
        return api;
      }
      if (this.isTerminalStatus(api) && this.notesBuildingMayFinish(visit.id)) {
        return api;
      }
      return 'Analyzing';
    }
    return this.displayStatus(visit.status);
  }

  isPipelineStatus(status: string): boolean {
    return status === 'Analyzing' || status === 'Transcribing' || status === 'In Progress';
  }

  private isTerminalStatus(status: string): boolean {
    return status === 'Completed' || status === 'Needs Review' || status === 'Failed';
  }

  displayStatus(status: string): string {
    const normalized = (status ?? '').toLowerCase().trim();
    switch (normalized) {
      case 'completed':
        return 'Completed';
      case 'needs review':
        return 'Needs Review';
      case 'paused':
        return 'Paused';
      case 'failed':
        return 'Failed';
      case 'analyzing':
        return 'Analyzing';
      case 'transcribing':
      case 'transcribed':
        return 'Transcribing';
      case 'in progress':
        return 'In Progress';
      default:
        return status?.trim() ? status : 'Not started';
    }
  }

  statusClass(status: string, visit?: Transcription): string {
    const s = visit ? this.cardStatus(visit) : this.displayStatus(status);
    if (s === 'Completed') return 'is-completed';
    if (this.isPipelineStatus(s) || s === 'Paused') return 'is-recording';
    if (s === 'Failed') return 'is-failed';
    if (s === 'Needs Review') return 'is-review';
    return 'is-draft';
  }

  patientLabel(v: Transcription): string {
    const name = (v.patientName ?? '').trim();
    if (this.isUnnamedPatientName(name)) {
      return 'Unknown';
    }
    return name || 'Unknown';
  }

  isUnknownPatient(v: Transcription): boolean {
    return this.isUnnamedPatientName((v.patientName ?? '').trim());
  }

  canEditPatientName(v: Transcription): boolean {
    return !isPendingRecordingVisitRow(v);
  }

  canDeleteVisit(v: Transcription): boolean {
    return !isPendingRecordingVisitRow(v);
  }

  isDeletingVisit(v: Transcription): boolean {
    return this.visitIdsEqual(this.deletingVisitId(), v.id);
  }

  deleteConfirmPatientLabel(): string {
    const visit = this.pendingDeleteVisit();
    return visit ? this.patientLabel(visit) : 'this visit';
  }

  onDeleteVisit(visit: Transcription, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canDeleteVisit(visit) || this.deletingVisitId() || this.savingVisitId()) {
      return;
    }
    this.cancelPatientNameEdit();
    this.deleteError.set(null);
    this.pendingDeleteVisit.set(visit);
    this.deleteConfirmVisible.set(true);
  }

  cancelDeleteConfirm(): void {
    if (this.deletingVisitId()) {
      return;
    }
    this.deleteConfirmVisible.set(false);
    this.pendingDeleteVisit.set(null);
  }

  confirmDeleteVisit(): void {
    const visit = this.pendingDeleteVisit();
    if (!visit || this.deletingVisitId()) {
      return;
    }
    this.deletingVisitId.set(String(visit.id));
    this.deleteError.set(null);
    this.transcriptionsService
      .deleteTranscription(visit.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          clearLocalStorageForPausedTranscriptionDraft(visit.originalFileName);
          this.deletingVisitId.set(null);
          this.deleteConfirmVisible.set(false);
          this.pendingDeleteVisit.set(null);
          const wasSelected = this.isSelected(visit);
          this.visits.update((list) => list.filter((v) => !this.visitIdsEqual(v.id, visit.id)));
          if (wasSelected) {
            const remaining = this.visitsForUi();
            if (this.isCompactShell()) {
              this.selectedId.set(null);
              this.clearNoteShell();
              this.mobileLanding.set(false);
            } else if (remaining[0]) {
              this.selectVisit(remaining[0]);
            } else {
              this.openNewVisitLanding();
            }
          }
          this.refreshVisits();
        },
        error: () => {
          this.deletingVisitId.set(null);
          this.deleteError.set('Could not delete this visit. Try again.');
        },
      });
  }

  isEditingPatientName(v: Transcription): boolean {
    return this.visitIdsEqual(this.editingVisitId(), v.id);
  }

  isSavingPatientName(v: Transcription): boolean {
    return this.visitIdsEqual(this.savingVisitId(), v.id);
  }

  onVisitCardClick(visit: Transcription): void {
    this.pinUserVisit(visit);
    this.selectVisit(visit);
  }

  onVisitCardKeydown(event: KeyboardEvent, visit: Transcription): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.pinUserVisit(visit);
      this.selectVisit(visit);
    }
  }

  /** Remember a visit the user opened so in-progress polling cannot steal it. */
  private pinUserVisit(visit: Transcription): void {
    this.userPinnedVisitId = String(visit.id);
    this.suppressProcessingAutoSelect = true;
  }

  /** Phone list: user backed out or opened another visit. Do not jump back to the in-progress row. */
  private keepCompactListSelection(): boolean {
    return this.isCompactShell() && this.suppressProcessingAutoSelect;
  }

  /**
   * True when this poll must not replace the current screen with the in-progress visit.
   * Desktop uses the pinned card. Mobile also stays on the list after Back.
   */
  private blockForcedProcessingSelection(
    forcedId: string,
    stillPresent: boolean,
    wasPending: boolean
  ): boolean {
    if (this.userPinnedSelectionBlocks(forcedId, stillPresent, wasPending)) {
      return true;
    }
    if (!this.keepCompactListSelection()) {
      return false;
    }
    const currentId = this.selectedId();
    if (currentId == null) {
      return true;
    }
    return !this.visitIdsEqual(currentId, forcedId);
  }

  /**
   * True when the user has a real visit selected and this poll wants a different row
   * (the in-progress / notes-building visit). Pending placeholder rows still resolve
   * to the real transcription when it appears.
   */
  private userPinnedSelectionBlocks(
    forcedId: string,
    stillPresent: boolean,
    wasPending: boolean
  ): boolean {
    const pin = this.userPinnedVisitId;
    const currentId = this.selectedId();
    if (pin == null || currentId == null || !stillPresent || wasPending) {
      return false;
    }
    if (!this.visitIdsEqual(pin, currentId) || this.visitIdsEqual(pin, forcedId)) {
      return false;
    }
    return !isPendingRecordingVisitRow({ id: pin });
  }

  startPatientNameEdit(visit: Transcription, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.canEditPatientName(visit) || this.savingVisitId()) {
      return;
    }
    const current = (visit.patientName ?? '').trim();
    this.editingVisitId.set(String(visit.id));
    this.editingPatientName.set(this.isUnnamedPatientName(current) ? '' : current);
    this.editingSelectedPatient.set(null);
    this.nameEditHighlight.set(0);
    this.nameEditError.set(null);
    this.nameEditDropdownOpen.set(false);
    setTimeout(() => this.patientNameEditInput?.nativeElement.focus(), 0);
  }

  cancelPatientNameEdit(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.editingVisitId.set(null);
    this.editingPatientName.set('');
    this.editingSelectedPatient.set(null);
    this.nameEditDropdownOpen.set(false);
    this.nameEditHighlight.set(0);
    this.nameEditError.set(null);
  }

  onEditingPatientNameChange(value: string): void {
    this.editingPatientName.set(value);
    this.nameEditDropdownOpen.set(value.trim().length > 0);
    this.nameEditHighlight.set(0);
    this.nameEditError.set(null);
    const selected = this.editingSelectedPatient();
    if (!selected) {
      return;
    }
    const fullName = `${selected.firstName} ${selected.lastName}`.trim().toLowerCase();
    if (value.trim().toLowerCase() !== fullName) {
      this.editingSelectedPatient.set(null);
    }
  }

  onNameEditKeydown(event: KeyboardEvent, visit: Transcription): void {
    const suggestions = this.nameEditSuggestions();
    const total = suggestions.length;
    const canShowSuggestions = this.editingPatientName().trim().length > 0 && total > 0;
    if (event.key === 'ArrowDown' && canShowSuggestions) {
      event.preventDefault();
      this.nameEditDropdownOpen.set(true);
      this.nameEditHighlight.set((this.nameEditHighlight() + 1) % total);
      return;
    }
    if (event.key === 'ArrowUp' && canShowSuggestions) {
      event.preventDefault();
      this.nameEditDropdownOpen.set(true);
      this.nameEditHighlight.set((this.nameEditHighlight() - 1 + total) % total);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.savePatientNameEdit(visit);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.nameEditDropdownOpen()) {
        this.nameEditDropdownOpen.set(false);
        return;
      }
      this.cancelPatientNameEdit();
    }
  }

  selectNameEditPatient(patient: PatientSummary, visit: Transcription): void {
    this.editingSelectedPatient.set(patient);
    this.editingPatientName.set(`${patient.firstName} ${patient.lastName}`.trim());
    this.nameEditDropdownOpen.set(false);
    this.nameEditError.set(null);
    this.savePatientNameEdit(visit);
  }

  savePatientNameEdit(visit: Transcription, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.isEditingPatientName(visit) || this.isSavingPatientName(visit)) {
      return;
    }
    const patientName = this.editingPatientName().trim();
    if (!patientName) {
      this.nameEditError.set('Enter a patient name.');
      return;
    }

    let patientId = this.editingSelectedPatient()?.id;
    if (patientId == null) {
      const typed = patientName.toLowerCase();
      const match = this.allPatients().find(
        (p) => `${p.firstName} ${p.lastName}`.trim().toLowerCase() === typed
      );
      if (match) {
        patientId = match.id;
      }
    }

    const appointmentType = (visit.appointmentType ?? '').trim() || 'Follow-up Visit';
    this.savingVisitId.set(String(visit.id));
    this.nameEditError.set(null);
    this.transcriptionsService
      .updateTranscriptionMetadata(visit.id, {
        patientName,
        appointmentType,
        patientId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.savingVisitId.set(null);
          this.syncVisitRowFromDetail(
            visit.id,
            {
              patientName: (detail?.patientName ?? patientName).trim(),
              patientId: detail?.patientId ?? patientId,
              mrn: detail?.mrn,
            },
            false
          );
          if (this.visitIdsEqual(this.editingVisitId(), visit.id)) {
            this.cancelPatientNameEdit();
          }
          this.loadPatientSummaries();
        },
        error: () => {
          this.savingVisitId.set(null);
          this.nameEditError.set('Could not update patient name. Try again.');
        },
      });
  }

  metaLine(v: Transcription): string {
    const time = this.formatTime(v);
    const duration = this.formatDurationMins(v.duration);
    const mrn = this.displayMrn(v);
    const reason = (v.visitReason ?? '').trim() || null;
    return [time, duration, mrn, reason].filter(Boolean).join(' · ');
  }

  detailWhenLine(v: Transcription): string {
    const when = this.formatDateAtTime(v);
    const duration = this.formatDurationMins(v.duration);
    if (when && duration) {
      return `${when} (${duration})`;
    }
    return when || duration || '';
  }

  showTranscriptLink(v: Transcription): boolean {
    return !isPendingRecordingVisitRow(v);
  }

  onTranscriptionClick(visit: Transcription, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.detailMode.set('transcript');
    this.loadTranscript(visit.id);
  }

  onTranscriptTabChange(tab: 'translated' | 'original'): void {
    this.transcriptTab.set(tab);
  }

  hasTranslatedTranscript(): boolean {
    return hasDistinctTranslation(
      this.translatedTranscript(),
      this.originalTranscript(),
      this.transcriptLanguage()
    );
  }

  displayedTranscript(): string | null {
    if (this.hasTranslatedTranscript() && this.transcriptTab() === 'translated') {
      return this.translatedTranscript();
    }
    if (this.hasTranslatedTranscript() && this.transcriptTab() === 'original') {
      return this.originalTranscript();
    }
    return this.originalTranscript() || this.translatedTranscript();
  }

  formatTranscriptHtml(transcript: string): string {
    return transcript.replace(/([.?!])\s+/g, '$1\n').trim().replace(/\n/g, '<br>');
  }

  async copyDisplayedTranscript(): Promise<void> {
    const text = this.displayedTranscript();
    if (!text) {
      return;
    }
    await this.copySection(text);
  }

  private loadTranscript(id: string): void {
    if (
      this.loadedTranscriptVisitId === id &&
      (this.transcriptLoading() || this.translatedTranscript() || this.originalTranscript())
    ) {
      return;
    }
    const requestId = ++this.transcriptRequestId;
    this.loadedTranscriptVisitId = id;
    this.transcriptLoading.set(true);
    this.translatedTranscript.set(null);
    this.originalTranscript.set(null);
    const language$ = this.transcriptLanguage()
      ? of(this.transcriptLanguage())
      : this.transcriptionsService.getTranscriptionById(id).pipe(
          map((detail) => detail?.transcriptLanguage ?? null),
          catchError(() => of(null))
        );
    forkJoin({
      translated: this.transcriptionsService
        .getTranscriptContent(id, 'translated')
        .pipe(catchError(() => of(null))),
      original: this.transcriptionsService
        .getTranscriptContent(id, 'original')
        .pipe(catchError(() => of(null))),
      language: language$,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ translated, original, language }) => {
          if (requestId !== this.transcriptRequestId) {
            return;
          }
          this.translatedTranscript.set(translated);
          this.originalTranscript.set(original);
          this.transcriptLanguage.set(language);
          this.transcriptTab.set(
            this.hasTranslatedTranscript() ? 'translated' : 'original'
          );
          this.transcriptLoading.set(false);
        },
        error: () => {
          if (requestId !== this.transcriptRequestId) {
            return;
          }
          this.transcriptLoading.set(false);
        },
      });
  }

  private displayMrn(v: Transcription): string | null {
    const mrn = (v.mrn ?? '').trim();
    if (!mrn || /^unknown(\s+mrn)?$/i.test(mrn)) {
      return null;
    }
    return mrn;
  }

  private isUnnamedPatientName(name: string): boolean {
    const n = name.trim().toLowerCase().replace(/\s+/g, ' ');
    return !n || n === 'unknown' || n === 'unknown patient' || n === 'unknown unknown';
  }

  private showDraftingShell(): void {
    this.draftingNote.set(true);
    this.pausedNote.set(false);
    this.sections.set([]);
    this.resetNoteEditorState();
  }

  private showPausedShell(): void {
    this.pausedNote.set(true);
    this.draftingNote.set(false);
    this.sections.set([]);
    this.resetNoteEditorState();
  }

  private clearNoteShell(): void {
    this.draftingNote.set(false);
    this.pausedNote.set(false);
    this.detailMode.set('notes');
    this.sections.set([]);
    this.transcriptLanguage.set(null);
    this.resetNoteEditorState();
  }

  private resetNoteEditorState(): void {
    this.editingSectionId.set(null);
    this.isSavingNotes.set(false);
    this.noteSaveError.set(null);
    this.originalSectionBodies.set({});
    this.soapProblems = [];
    this.keyFindings = null;
    this.aiNotesLoaded = false;
  }

  private captureOriginalBodies(list: NoteSection[], replace = false): void {
    this.originalSectionBodies.update((prev) => {
      const next = replace ? {} : { ...prev };
      for (const s of list) {
        if (replace || !(s.id in next)) {
          next[s.id] = s.body;
        }
      }
      return next;
    });
  }

  private buildNotesPayload(): SaveTranscriptionClinicalNotesPayload {
    const byId = new Map(this.sections().map((s) => [s.id, s.body]));
    const apText = byId.get('assessment-plan');
    const parsed =
      apText != null
        ? parseCombinedAssessmentAndPlan(apText, this.soapProblems)
        : { summary: '', problems: this.soapProblems };
    const problems = parsed.problems.length ? parsed.problems : this.soapProblems;
    this.soapProblems = problems;

    const payload: SaveTranscriptionClinicalNotesPayload = {
      subjective: byId.get('hpi') ?? '',
      objective: byId.get('objective') ?? '',
      patientInstructions: byId.get('patient-instructions') ?? '',
      problems,
      assessmentSummary: parsed.summary,
    };
    if (this.keyFindings !== null) {
      payload.keyFindings = this.keyFindings;
    }
    if (this.aiNotesLoaded || byId.has('treatment-plan-summary')) {
      payload.treatmentPlanSummary = this.bulletTextToLines(
        byId.get('treatment-plan-summary') ?? ''
      );
    }
    return payload;
  }

  private bulletTextToLines(text: string): string[] {
    if (!text?.trim()) {
      return [];
    }
    return text
      .split('\n')
      .map((line) => line.replace(/^\s*[•\-*]\s*/, '').trim())
      .filter(Boolean);
  }

  private autoResizeTextarea(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  private resizeEditingTextarea(): void {
    this.soapTextareas?.forEach((ref) => this.autoResizeTextarea(ref.nativeElement));
  }

  private loadAiAnalysisSections(id: string, requestId: number): void {
    this.transcriptionDetailService
      .loadAIAnalysis(id)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe((content) => {
        if (requestId !== this.detailRequestId || !this.visitIdsEqual(this.selectedId(), id)) {
          return;
        }
        if (!content) {
          return;
        }
        this.aiNotesLoaded = true;
        this.keyFindings = (content.keyFindings ?? [])
          .map((s) => (s ?? '').trim())
          .filter(Boolean);
        const extras = this.aiAnalysisToSections(content);
        if (!extras.length) {
          return;
        }
        this.sections.update((list) => {
          const have = new Set(list.map((s) => s.id));
          const toAdd = extras.filter((e) => !have.has(e.id));
          if (!toAdd.length) {
            return list;
          }
          this.captureOriginalBodies(toAdd);
          return [...list, ...toAdd];
        });
      });
  }

  private aiAnalysisToSections(content: AIAnalysisContent | null): NoteSection[] {
    if (!content) {
      return [];
    }
    const extras: NoteSection[] = [];
    const treatment = this.formatBulletLines(content.treatmentPlanSummary).trim();
    const isDummyTreatment =
      (content.treatmentPlanSummary ?? []).length === 1 &&
      (content.treatmentPlanSummary?.[0] ?? '').includes(
        'Please use the SOAP Notes section for treatment planning'
      );
    if (treatment && !isDummyTreatment) {
      extras.push({
        id: 'treatment-plan-summary',
        title: 'Treatment Plan Summary',
        body: treatment,
        collapsed: false,
        includeInCopyAll: false,
      });
    }

    const icd = this.formatIcdCodes(content.suggestedICD10Codes);
    if (icd) {
      extras.push({
        id: 'icd-10',
        title: 'ICD-10 Codes',
        body: icd,
        collapsed: false,
        includeInCopyAll: false,
      });
    }
    return extras;
  }

  private formatBulletLines(items: string[] | null | undefined): string {
    if (!items?.length) {
      return '';
    }
    return items
      .map((s) => (s ?? '').trim())
      .filter(Boolean)
      .map((s) => `• ${s}`)
      .join('\n');
  }

  private formatIcdCodes(
    codes: Array<{ code?: string; description?: string }> | null | undefined
  ): string {
    if (!codes?.length) {
      return '';
    }
    return codes
      .filter((c) => !!c?.code)
      .map((c) => {
        const code = (c.code ?? '').trim();
        const desc = (c.description ?? '').trim();
        return desc ? `${code} - ${desc}` : code;
      })
      .filter(Boolean)
      .join('\n');
  }

  /** Status body on a failed visit. A missing transcript is explained; Java exception text is not shown. */
  private userFacingVisitError(error: string | null | undefined): string {
    const text = error?.trim() ?? '';
    if (/cannot invoke|nullpointer|lastindexof|transcripturl|missing original|no transcript/i.test(text)) {
      return "Transcription can't be re-analyzed due to missing original.";
    }
    return text || 'Clinical notes are not available for this visit.';
  }

  private loadDetail(id: string): void {
    if (isPendingRecordingVisitRow({ id }) || id === PENDING_RECORDING_VISIT_ID) {
      this.showDraftingShell();
      this.detailLoading.set(false);
      return;
    }
    const requestId = ++this.detailRequestId;
    const listVisit = this.visits().find((v) => this.visitIdsEqual(v.id, id));
    const listStatus = listVisit ? this.cardStatus(listVisit) : '';
    if (listStatus === 'Paused' && !this.transcriptionsService.isNotesBuilding(id)) {
      this.showPausedShell();
      this.detailLoading.set(false);
      return;
    }
    if (this.transcriptionsService.isNotesBuilding(id) || this.isPipelineStatus(listStatus)) {
      this.showDraftingShell();
      this.detailLoading.set(false);
    } else if (!this.draftingNote() && !this.pausedNote()) {
      this.detailLoading.set(true);
    }

    this.transcriptionsService
      .getTranscriptionById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          if (requestId !== this.detailRequestId || !this.visitIdsEqual(this.selectedId(), id)) {
            return;
          }
          this.detailLoading.set(false);
          this.transcriptLanguage.set(detail?.transcriptLanguage ?? null);
          if (!detail || isFailedTranscriptionStatus(detail.status)) {
            this.transcriptionsService.clearNotesBuilding(id);
            this.clearNoteShell();
            this.sections.set([
              {
                id: 'status',
                title: 'Status',
                body: this.userFacingVisitError(detail?.error),
                collapsed: false,
              },
            ]);
            return;
          }

          const status = this.displayStatus(detail.status);
          const soap = detail.analysis?.soapNote;
          const noteReady = this.hasSubstantiveSoap(soap);
          const building = this.transcriptionsService.isNotesBuilding(id);

          if (status === 'Paused' && !building) {
            this.showPausedShell();
            this.syncVisitRowFromDetail(id, detail, false);
            return;
          }

          if (this.isPipelineStatus(status)) {
            this.transcriptionsService.markNotesBuildingPipelineSeen(id);
            this.showDraftingShell();
            this.syncVisitRowFromDetail(id, detail, false);
            return;
          }

          // Rebuild (append/reanalyze): keep the wave until Analyzing has been seen, then Completed.
          // New recording: Completed + SOAP is enough even if we missed the Analyzing poll.
          if (building) {
            const terminal = status === 'Completed' || status === 'Needs Review';
            if (noteReady && terminal && this.notesBuildingMayFinish(id)) {
              this.transcriptionsService.clearNotesBuilding(id);
            } else {
              this.showDraftingShell();
              this.syncVisitRowFromDetail(id, detail, false);
              return;
            }
          }

          // In-progress pipeline, or completed row that still has no note body yet.
          if (!noteReady && status !== 'Failed') {
            this.showDraftingShell();
            this.syncVisitRowFromDetail(id, detail, false);
            return;
          }

          this.clearNoteShell();
          const next: NoteSection[] = [];
          this.soapProblems = soap?.problems ?? [];

          const subjectiveSections = foldSubjectiveIntoHpiAndRos(
            splitSoapByHeadings(soap?.subjective, SUBJECTIVE_HEADING_SPECS, {
              id: 'hpi',
              title: 'History of Present Illness',
            })
          );
          const chartName = this.selected()?.patientName;
          for (const s of subjectiveSections) {
            next.push({
              ...s,
              body: applyChartPatientName(
                flattenSubjectiveStoryLabels(stripLeadingSectionNumbers(s.body)),
                chartName
              ),
              collapsed: false,
            });
          }

          // If HPI is missing entirely, fall back to list-level chief complaint as the HPI body.
          const hasHpi = next.some((s) => s.id === 'hpi');
          const chief = this.selected()?.chiefComplaint?.trim();
          if (!hasHpi && chief) {
            next.unshift({
              id: 'hpi',
              title: 'History of Present Illness',
              body: applyChartPatientName(chief, this.selected()?.patientName),
              collapsed: false,
            });
          }

          // Always show Objective as one section (even when empty). Strip "1. Vitals" style numbering.
          const objectiveBody =
            soap?.objective?.trim() && !/^n\/a$/i.test(soap.objective.trim())
              ? foldObjectiveForDisplay(soap.objective.trim())
              : '';
          next.push({
            id: 'objective',
            title: 'Objective',
            body: objectiveBody,
            collapsed: false,
          });

          const problems = soap?.problems ?? [];
          if (problems.length) {
            next.push({
              id: 'assessment-plan',
              title: 'Assessment and Plan',
              body: applyChartPatientName(
                formatCombinedAssessmentAndPlan(
                  problems,
                  soap?.assessmentSummary,
                  this.selected()?.patientName
                ),
                this.selected()?.patientName
              ),
              collapsed: false,
            });
          }

          if (soap?.patientInstructions?.trim()) {
            next.push({
              id: 'patient-instructions',
              title: 'Patient Instructions',
              body: foldPatientInstructionsForDisplay(
                soap.patientInstructions.trim(),
                this.selected()?.patientName
              ),
              collapsed: false,
            });
          }

          if (!next.length) {
            next.push({
              id: 'empty',
              title: 'Clinical notes',
              body: 'No note sections yet.',
              collapsed: false,
            });
          }
          this.sections.set(next);
          this.captureOriginalBodies(next, true);
          this.loadAiAnalysisSections(id, requestId);
          this.syncVisitRowFromDetail(id, {
            ...detail,
            visitReason: visitReasonFromSoap(soap) ?? detail.visitReason,
          }, true);
          this.acknowledgeSoapNotesViewed(id, detail);
        },
        error: () => {
          if (requestId !== this.detailRequestId || !this.visitIdsEqual(this.selectedId(), id)) {
            return;
          }
          this.detailLoading.set(false);
          this.clearNoteShell();
          this.sections.set([
            {
              id: 'error',
              title: 'Clinical notes',
              body: 'Unable to load note details.',
              collapsed: false,
            },
          ]);
        },
      });
  }

  /** Keep the left-rail visit card status (and name) in sync with detail once notes are ready. */
  private syncVisitRowFromDetail(
    id: string,
    detail: {
      status?: string;
      patientName?: string;
      patientId?: string | number;
      chiefComplaint?: string;
      visitReason?: string;
      duration?: string;
      mrn?: string;
    },
    noteReady: boolean
  ): void {
    this.visits.update((list) =>
      list.map((v) => {
        if (!this.visitIdsEqual(v.id, id)) {
          return v;
        }
        let status = (detail.status ?? v.status).trim() || v.status;
        // Notes are on screen but list API may still say Analyzing / In Progress.
        if (noteReady && this.isPipelineStatus(this.displayStatus(status))) {
          status = 'Completed';
        }
        // Keep optimistic Analyzing while a rebuild is still waiting to leave Completed.
        if (
          this.transcriptionsService.isNotesBuilding(id) &&
          !this.isPipelineStatus(this.displayStatus(status)) &&
          !this.notesBuildingMayFinish(id)
        ) {
          status = 'Analyzing';
        }
        const detailPatientId =
          detail.patientId != null && String(detail.patientId).trim()
            ? String(detail.patientId).trim()
            : undefined;
        return {
          ...v,
          status,
          patientName: (detail.patientName ?? '').trim() || v.patientName,
          patientId: detailPatientId || v.patientId,
          chiefComplaint: (detail.chiefComplaint ?? '').trim() || v.chiefComplaint,
          visitReason: (detail.visitReason ?? '').trim() || v.visitReason,
          duration: (detail.duration ?? '').trim() || v.duration,
          mrn: (detail.mrn ?? '').trim() || v.mrn,
        };
      })
    );
  }

  /** Clears unread bold on the list after SOAP notes are shown. */
  private acknowledgeSoapNotesViewed(
    id: string,
    detail: { soapNotesAvailable?: boolean; soapUnread?: boolean }
  ): void {
    const listVisit = this.visits().find((v) => this.visitIdsEqual(v.id, id));
    const unread = detail.soapUnread === true || listVisit?.soapUnread === true;
    if (!unread || detail.soapNotesAvailable === false) {
      return;
    }
    this.acknowledgedSoapVisitIds.add(id);
    this.visits.update((list) =>
      list.map((v) => (this.visitIdsEqual(v.id, id) ? { ...v, soapUnread: false } : v))
    );
    // Do not takeUntil(destroy$): a refresh/navigation would cancel the POST and leave notes unread.
    this.transcriptionsService.markSoapNotesRead(id).subscribe({
      error: () => {},
    });
  }

  private hasSubstantiveSoap(
    soap:
      | {
          subjective?: string;
          objective?: string;
          patientInstructions?: string;
          problems?: { name?: string }[];
        }
      | null
      | undefined
  ): boolean {
    if (!soap) {
      return false;
    }
    const text = (v?: string) => {
      const t = (v ?? '').trim();
      return t.length > 0 && !/^n\/a$/i.test(t);
    };
    if (text(soap.subjective) || text(soap.objective) || text(soap.patientInstructions)) {
      return true;
    }
    return (soap.problems ?? []).some((p) => (p.name ?? '').trim().length > 0);
  }

  private visitIdsEqual(
    a: string | number | null | undefined,
    b: string | number | null | undefined
  ): boolean {
    if (a == null || b == null) {
      return false;
    }
    return String(a).trim() === String(b).trim();
  }

  private visitInstant(v: Transcription): Date | null {
    return parseApiDateTime(v.appointmentTime ?? v.date);
  }

  private visitTime(v: Transcription): number {
    return this.visitInstant(v)?.getTime() ?? 0;
  }

  private dayKey(v: Transcription): string {
    const d = this.visitInstant(v);
    if (!d) return 'Unknown date';
    return formatLocalDate(d);
  }

  private formatTime(v: Transcription): string {
    const d = this.visitInstant(v);
    return d ? formatLocalTime(d) : '';
  }

  private formatDateAtTime(v: Transcription): string {
    const d = this.visitInstant(v);
    return d ? formatLocalDateAtTime(d) : '';
  }

  /** Stored duration is MM:SS (or HH:MM:SS); list/header show whole minutes. */
  private formatDurationMins(raw: string | undefined): string | null {
    const s = (raw ?? '').trim();
    if (!s || /^(n\/a|unknown)$/i.test(s)) {
      return null;
    }
    const already = s.match(/^(\d+)\s*mins?$/i);
    if (already) {
      return `${Number(already[1])} mins`;
    }
    const parts = s.split(':').map((p) => Number(p));
    if (!parts.length || parts.some((n) => !Number.isFinite(n))) {
      return s;
    }
    let seconds = 0;
    if (parts.length === 2) {
      seconds = parts[0] * 60 + parts[1];
    } else if (parts.length === 3) {
      seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else {
      return s;
    }
    if (seconds <= 0) {
      return null;
    }
    return `${Math.max(1, Math.round(seconds / 60))} mins`;
  }
}
